import type { ReactNode } from 'react';

import { NotFound } from '#/components/ui/NotFound';
import { Spinner } from '#/components/ui/Spinner';
import {
  getApiErrorDisplay,
  isNotFoundError,
  isSuccessResponse,
} from '#/lib/api-client';
import { useSkeletonMode } from '#/lib/skeleton-mode';

/**
 * Minimal shape of the response envelope every Orval-generated hook
 */
interface ApiEnvelope {
  status: number;
}

interface QueryBoundaryResult<TResponse, TError = unknown> {
  isPending: boolean;
  isError: boolean;
  error: TError | null;
  data: TResponse | undefined;
}

type NotFoundRenderer<TError> = (error: TError | null | undefined) => ReactNode;

interface QueryBoundaryProps<TResponse extends ApiEnvelope, TError = unknown> {
  query: QueryBoundaryResult<TResponse, TError>;
  /**
   * Rendered while the query is pending. Defaults to a full-width Spinner.
   * Pages that own a layout pass a skeleton shaped like it instead —
   * `loading={<DetailSkeleton category="equities" />}`.
   */
  loading?: ReactNode;
  /** Rendered on a 404 (or a non-200 envelope). Defaults to <NotFound />. */
  notFound?: ReactNode | NotFoundRenderer<TError>;
  /** Rendered for non-404 errors. Defaults to a generic error message. */
  renderError?: (error: TError) => ReactNode;
  /** Receives the success envelope (status 200), narrowed from the union. */
  children: (data: Extract<TResponse, { status: 200 }>) => ReactNode;
}

/**
 * Brackets a single TanStack Query result and resolves the pending / error /
 * not-found / success states in one place
 */
export function QueryBoundary<TResponse extends ApiEnvelope, TError = unknown>({
  query,
  loading = <Spinner />,
  notFound = <NotFound />,
  renderError,
  children,
}: QueryBoundaryProps<TResponse, TError>) {
  // Dev-only: holds the loading branch open so a skeleton can be compared
  // against the real page. Always false in production (`#/lib/skeleton-mode`).
  const skeletonMode = useSkeletonMode();

  const renderNotFound = (error: TError | null | undefined) => {
    if (typeof notFound === 'function') {
      return notFound(error);
    }

    return notFound;
  };

  if (query.isPending || skeletonMode) {
    return loading;
  }

  if (query.isError) {
    const error = query.error;

    if (isNotFoundError(error)) {
      return renderNotFound(error);
    }
    if (renderError && error !== null) {
      return renderError(error);
    }

    const errorDisplay = getApiErrorDisplay(error);

    return (
      <div role="alert" className="p-8">
        <p className="text-error font-medium">{errorDisplay.title}</p>
        <p className="text-fg-muted mt-4">{errorDisplay.message}</p>
      </div>
    );
  }

  if (!isSuccessResponse(query.data)) {
    return renderNotFound(undefined);
  }

  return children(query.data);
}
